import * as PropTypes from 'prop-types'
import React, { useMemo } from 'react'

function CreditApplicationModal({ creditApplicationData, creditApplicaitonRef, setNewModal }) {
  const {
    applicant_section: application,
    address_section: homeAddress,
    license_section: driverLicense,
    job_section: jobInformation,
    income_section: additionalIncome,
    references
  } = creditApplicationData || {}

  const applicationFormValues = useMemo(
    () => [
      {
        label: 'First Name',
        value: application?.first_name
      },
      {
        label: 'Middle Name',
        value: application?.middle_name
      },
      {
        label: 'Last Name',
        value: application?.last_name
      },
      {
        label: 'Email',
        value: application?.email
      },
      {
        label: 'Date of Birth',
        value: application?.date_of_birth
      },
      {
        label: 'Social Security Number',
        value: application?.social_security_number
      },
      {
        label: 'Mobile Number',
        value: application?.mobile_number
      },
      {
        label: 'Marital Status',
        value: application?.marital_status
      },
      {
        label: 'Citizenship Status',
        value: application?.citizenship_status
      }
    ],
    [application]
  )

  const homeAddressFormValues = useMemo(
    () => [
      {
        label: 'Street Address',
        value: homeAddress?.street_address
      },
      {
        label: 'Zip Code',
        value: homeAddress?.zip_code
      },
      {
        label: 'City',
        value: homeAddress?.city
      },
      {
        label: 'State',
        value: homeAddress?.state
      },
      {
        label: 'County',
        value: homeAddress?.county
      },
      {
        label: 'Years at Address',
        value: homeAddress?.years_at_address
      },
      {
        label: 'Months at Address',
        value: homeAddress?.months_at_address
      },
      {
        label: 'Residential Status',
        value: homeAddress?.residential_status
      },
      {
        label: 'State',
        value: homeAddress?.state
      }
    ],
    [homeAddress]
  )

  const driverLicenseFormValues = useMemo(
    () => [
      {
        label: 'License Number',
        value: driverLicense?.license_number
      },
      {
        label: 'License State',
        value: driverLicense?.license_state
      },
      {
        label: 'License Issued',
        value: driverLicense?.license_issued_date
      },
      {
        label: 'License Expiry',
        value: driverLicense?.license_expiry_date
      }
    ],
    [driverLicense]
  )

  const jobInformationFormValues = useMemo(
    () => [
      {
        label: 'Employer Name',
        value: jobInformation?.employer_name
      },
      {
        label: 'Job Status',
        value: jobInformation?.job_status
      },
      {
        label: 'Annual Gross Income',
        value: jobInformation?.annual_gross_income
      },
      {
        label: 'Annual Net Income',
        value: jobInformation?.annual_net_income
      },
      {
        label: 'Work Phone',
        value: jobInformation?.work_phone
      },
      {
        label: 'Job Title',
        value: jobInformation?.job_title
      },
      {
        label: 'Street Address',
        value: jobInformation?.street_address
      },
      {
        label: 'Zip Code',
        value: jobInformation?.zipCode
      },
      {
        label: 'City',
        value: jobInformation?.city
      },
      {
        label: 'State',
        value: jobInformation?.state
      },
      {
        label: 'County',
        value: jobInformation?.county
      },
      {
        label: 'Years At Job',
        value: jobInformation?.years_at_job
      },
      {
        label: 'Months At Job',
        value: jobInformation?.months_at_job
      },
      {
        label: 'Previous Home Address',
        value: jobInformation?.previous_home_address
      },
      {
        label: 'Previous Job',
        value: jobInformation?.previous_job
      },
      {
        label: 'Job Type',
        value: jobInformation?.job_type
      }
    ],
    [jobInformation]
  )

  const additionalIncomeFormValues = useMemo(
    () => [
      {
        label: 'Additional Annual Income',
        value: additionalIncome?.additional_annual_income
      },
      {
        label: 'Additional Annual Source',
        value: additionalIncome?.additional_income_source
      }
    ],
    [additionalIncome]
  )

  const referencesFormValues = useMemo(() => {
    return references?.map((e) => [
      {
        label: 'First Name',
        value: e.first_name
      },
      {
        label: 'Last Name',
        value: e.last_name
      },
      {
        label: 'Phone',
        value: e.phone
      },
      {
        label: 'Street Addess',
        value: e.street_address
      },
      {
        label: 'Zip Code',
        value: e.zipCode
      },
      {
        label: 'City',
        value: e.city
      },
      {
        label: 'State',
        value: e.state
      },
      {
        label: 'County',
        value: e.county
      },
      {
        label: 'Relationship',
        value: e.relationship
      }
    ])
  }, [references])

  const additionalDataRender = useMemo(() => {
    if (referencesFormValues?.length < 2) return []
    return referencesFormValues
      ?.slice(1)
      .map((e, idx) => ({
        name: `References ${idx + 2}`,
        data: e
      }))
      .reduce((all, one, i) => {
        const ch = Math.floor(i / 6)
        all[ch] = [].concat(all[ch] || [], one)
        return all
      }, [])
  }, [referencesFormValues])

  const dataRenderer = useMemo(() => {
    const allForm = [
      { name: 'Applicant', data: applicationFormValues },
      { name: 'Home Address', data: homeAddressFormValues },
      { name: 'Driver License', data: driverLicenseFormValues },
      { name: 'Additional Income', data: additionalIncomeFormValues },
      { name: 'Job Information', data: jobInformationFormValues }
    ]

    if (referencesFormValues?.length > 0) {
      allForm.push({
        name: `References 1`,
        data: referencesFormValues[0]
      })
    }
    return allForm
  }, [
    applicationFormValues,
    homeAddressFormValues,
    driverLicenseFormValues,
    additionalIncomeFormValues,
    jobInformationFormValues,
    referencesFormValues
  ])

  return (
    <div ref={creditApplicaitonRef}>
      <div className="flex justify-between items-center px-4 pt-4">
        <div className="text-2xl font-bold">Credit Application Detail</div>
        <div className="text-base uppercase cursor-pointer" onClick={() => setNewModal(false)}>
          Close
        </div>
      </div>
      <div className="grid grid-cols-2 pt-4 px-4">
        {dataRenderer?.map((step, key) => {
          return (
            <div className="m-2" key={`data-block-${key}`}>
              <div className="mb-2 font-bold text-xl">{step.name}</div>
              {step.data?.map((e, idx) => {
                if (!e.value) return null
                return (
                  <div className="my-1" key={`data-${key}-section-${idx}`}>
                    <span className="text-md">{e.label}: </span>
                    <span className="text-md">{e.value || ''}</span>
                  </div>
                )
              })}
            </div>
          )
        })}
      </div>

      <div className="print-page-container" />
      {additionalDataRender?.map((page, key) => {
        return (
          <div className="grid grid-cols-2 pt-4 pl-4 print-page-container" key={`page-${key}-additional-data`}>
            {page?.map((step, key) => {
              return (
                <div className="m-2 " key={`data-block-${key}`}>
                  <div className="mb-2 font-bold text-xl">{step.name}</div>
                  {step.data?.map((e, idx) => {
                    if (!e.value) return null
                    return (
                      <div className="my-1" key={`data-${key}-section-${idx}`}>
                        <span className="text-md">{e.label}: </span>
                        <span className="text-md">{e.value || ''}</span>
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

CreditApplicationModal.propTypes = {
  creditApplicationData: PropTypes.object,
  creditApplicaitonRef: PropTypes.object,
  setNewModal: PropTypes.func
}

export default CreditApplicationModal
